import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { Row } from 'react-table'

import type { ReviewEntryItem } from '../types/reviewEntryItem'
import type { ReviewEntryLine } from '../types/reviewEntryLine'
import type { ReviewEntryTableRow } from '../types/reviewEntryTableRow'
import { getReviewEntryContactId } from '../utils/getReviewEntryContactId'
import { getReviewEntryId } from '../utils/getReviewEntryId'
import { getReviewEntrySourceFingerprint } from '../utils/getReviewEntrySourceFingerprint'
import { getSelectedEntryRowWithLiveStatus } from '../utils/getSelectedEntryRowWithLiveStatus'
import { isReviewEntryProcessingStatus } from '../utils/isReviewEntryProcessingStatus'

interface UseReviewEntryDetailPanelProps {
  items: ReviewEntryItem[]
  rows: ReviewEntryTableRow[]
}

interface UseReviewEntryDetailPanelResult {
  dismissedClarificationKeys: string[]
  dismissedClarificationKeysByEntryId: Record<string, string[]>
  handleDetailClose: () => void
  handleDismissClarification: (entryId: string, questionKey: string) => void
  handleNavigateNext: () => void
  handleNavigatePrevious: () => void
  handleRowClick: (row: Row<ReviewEntryTableRow>) => void
  isDetailOpen: boolean
  selectedDocumentId: string | null
  selectedEntryIndex: number | null
  selectedEntryLines: ReviewEntryLine[]
  selectedEntryRow: ReviewEntryTableRow | null
  selectedEntryReference: string | null
  selectedInvoiceNumber: string | null
  selectedJournalCode: string | null
  selectedSupplierId: string | null
  sourceFingerprint?: string
  totalCount: number
}

export const useReviewEntryDetailPanel = ({
  items,
  rows,
}: UseReviewEntryDetailPanelProps): UseReviewEntryDetailPanelResult => {
  const [isDetailOpen, setIsDetailOpen] = useState(false)
  const [selectedEntryRow, setSelectedEntryRow] = useState<ReviewEntryTableRow | null>(null)
  const [dismissedClarificationKeysByEntryId, setDismissedClarificationKeysByEntryId] = useState<
    Record<string, string[]>
  >({})

  const dismissedClarificationKeys = useMemo(() => {
    if (!selectedEntryRow) {
      return []
    }

    return dismissedClarificationKeysByEntryId[selectedEntryRow.entryId] ?? []
  }, [dismissedClarificationKeysByEntryId, selectedEntryRow])

  const handleDismissClarification = useCallback((entryId: string, questionKey: string) => {
    setDismissedClarificationKeysByEntryId((current) => {
      const entryDismissedKeys = current[entryId] ?? []

      if (entryDismissedKeys.includes(questionKey)) {
        return current
      }

      return {
        ...current,
        [entryId]: [...entryDismissedKeys, questionKey],
      }
    })
  }, [])

  // prune dismissed-clarification state for entries that have left `rows` (validated or
  // filtered out) so the map stays bounded by the current list rather than growing for the
  // full session
  useEffect(() => {
    const currentEntryIds = new Set(rows.map((row) => row.entryId))

    setDismissedClarificationKeysByEntryId((current) => {
      const staleEntryIds = Object.keys(current).filter((entryId) => !currentEntryIds.has(entryId))

      if (staleEntryIds.length === 0) {
        return current
      }

      const next = { ...current }
      for (const entryId of staleEntryIds) {
        delete next[entryId]
      }

      return next
    })
  }, [rows])

  const navigableRows = useMemo(
    () => rows.filter((r) => r.showDocumentLevelFields && !isReviewEntryProcessingStatus(r.status)),
    [rows],
  )

  const selectedEntryIndex = useMemo(() => {
    if (!selectedEntryRow) {
      return null
    }
    const index = navigableRows.findIndex((r) => r.entryId === selectedEntryRow.entryId)

    return index >= 0 ? index : null
  }, [selectedEntryRow, navigableRows])

  const anchorSnapshotRef = useRef<{ entryIds: string[]; index: number } | null>(null)
  useEffect(() => {
    if (selectedEntryIndex !== null) {
      anchorSnapshotRef.current = {
        entryIds: navigableRows.map((r) => r.entryId),
        index: selectedEntryIndex,
      }
    }
  }, [selectedEntryIndex, navigableRows])

  const selectedEntryRowWithLiveStatus = useMemo(
    () => getSelectedEntryRowWithLiveStatus({ items, rows, selectedEntryRow }),
    [items, rows, selectedEntryRow],
  )

  const lastSelectedEntryItemRef = useRef<{ entryId: string; item: ReviewEntryItem } | null>(null)
  const selectedEntryItem = useMemo(() => {
    if (!selectedEntryRowWithLiveStatus) {
      return null
    }

    const liveItem =
      items.find((item, itemIndex) => getReviewEntryId(item, itemIndex) === selectedEntryRowWithLiveStatus.entryId) ??
      null

    if (liveItem) {
      return liveItem
    }

    // a refetch can briefly empty `items` while the panel stays open; keep the last item so
    // the form fingerprint does not flicker to undefined and wipe the draft
    if (items.length > 0) {
      return null
    }

    const cachedItem = lastSelectedEntryItemRef.current

    if (cachedItem?.entryId === selectedEntryRowWithLiveStatus.entryId) {
      return cachedItem.item
    }

    return null
  }, [items, selectedEntryRowWithLiveStatus])

  if (selectedEntryItem && selectedEntryRowWithLiveStatus) {
    lastSelectedEntryItemRef.current = {
      entryId: selectedEntryRowWithLiveStatus.entryId,
      item: selectedEntryItem,
    }
  } else if (!isDetailOpen || items.length > 0) {
    lastSelectedEntryItemRef.current = null
  }

  const selectedEntryLines = useMemo(() => selectedEntryItem?.entryLines ?? [], [selectedEntryItem])

  const selectedSupplierId = useMemo(
    () => (selectedEntryItem ? getReviewEntryContactId(selectedEntryItem.processingDecision) : null),
    [selectedEntryItem],
  )

  const selectedInvoiceNumber = selectedEntryItem?.reference ?? selectedEntryItem?.documentName ?? null
  const selectedEntryReference = selectedEntryItem?.reference ?? null
  const selectedJournalCode = selectedEntryItem?.journalCode ?? null
  const sourceFingerprint = selectedEntryItem ? getReviewEntrySourceFingerprint(selectedEntryItem) : undefined

  const handleRowClick = useCallback(
    (row: Row<ReviewEntryTableRow>) => {
      const headerRow = rows.find((r) => r.entryId === row.original.entryId && r.showDocumentLevelFields)
      const entry = headerRow ?? row.original

      if (isReviewEntryProcessingStatus(entry.status)) {
        return
      }

      setSelectedEntryRow(entry)
      setIsDetailOpen(true)
    },
    [rows],
  )

  const handleDetailClose = useCallback(() => {
    setIsDetailOpen(false)
    anchorSnapshotRef.current = null
  }, [])

  const handleNavigatePrevious = useCallback(() => {
    if (selectedEntryIndex !== null) {
      if (selectedEntryIndex <= 0) {
        return
      }
      setSelectedEntryRow(navigableRows[selectedEntryIndex - 1])
      return
    }

    // Selected entry is no longer in `navigableRows`. Walk back through the last-known
    // snapshot; the first predecessor still present in the current list is the true prior
    // entry, regardless of whether the list changed via validate or via filters.
    const snapshot = anchorSnapshotRef.current
    if (snapshot === null) {
      return
    }
    const currentIndexById = new Map(navigableRows.map((r, i) => [r.entryId, i]))
    for (let i = snapshot.index - 1; i >= 0; i -= 1) {
      const nextIdx = currentIndexById.get(snapshot.entryIds[i])
      if (nextIdx !== undefined) {
        setSelectedEntryRow(navigableRows[nextIdx])
        return
      }
    }
  }, [selectedEntryIndex, navigableRows])

  const handleNavigateNext = useCallback(() => {
    if (selectedEntryIndex !== null) {
      if (selectedEntryIndex >= navigableRows.length - 1) {
        return
      }
      setSelectedEntryRow(navigableRows[selectedEntryIndex + 1])
      return
    }

    // Selected entry is no longer in `navigableRows`. Walk forward through the last-known
    // snapshot to find the first successor still present in the current list. In the ADVP-238
    // validate case this is the entry that shifted up into the removed slot; in the filter
    // case it may be further along, or not exist at all (in which case we no-op instead of
    // selecting an unrelated document).
    const snapshot = anchorSnapshotRef.current
    if (snapshot === null) {
      return
    }
    const currentIndexById = new Map(navigableRows.map((r, i) => [r.entryId, i]))
    for (let i = snapshot.index + 1; i < snapshot.entryIds.length; i += 1) {
      const nextIdx = currentIndexById.get(snapshot.entryIds[i])
      if (nextIdx !== undefined) {
        setSelectedEntryRow(navigableRows[nextIdx])
        return
      }
    }
  }, [selectedEntryIndex, navigableRows])

  return {
    dismissedClarificationKeys,
    dismissedClarificationKeysByEntryId,
    handleDetailClose,
    handleDismissClarification,
    handleNavigateNext,
    handleNavigatePrevious,
    handleRowClick,
    isDetailOpen,
    selectedDocumentId: selectedEntryItem?.documentId ?? null,
    selectedEntryIndex,
    selectedEntryLines,
    selectedEntryRow: selectedEntryRowWithLiveStatus,
    selectedEntryReference,
    selectedInvoiceNumber,
    selectedJournalCode,
    selectedSupplierId,
    sourceFingerprint,
    totalCount: navigableRows.length,
  }
}
